import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { createImageWarmer, findVariantMedia, shouldPreserveGallery } from '@theme/variant-media';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.dataset.hideVariants === 'true' && target) {
      target.addEventListener('pointerenter', this.#warmProspectiveOption, { capture: true, signal });
      target.addEventListener('focusin', this.#warmProspectiveOption, { signal });
      if (document.readyState === 'complete') {
        this.#scheduleWarmCandidates();
      } else {
        window.addEventListener('load', this.#scheduleWarmCandidates, { once: true, signal });
      }
    }
  }

  #controller = new AbortController();
  #warmer = createImageWarmer(Image, 2);
  #mediaGeneration = 0;
  #latestVariantId = '';
  #localMediaFailed = false;
  /** @type {{ generation: number, gallery: Element } | null} */
  #pendingServerGallery = null;
  /** @type {number | undefined} */
  #warmTimer;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#mediaGeneration++;
    this.#pendingServerGallery = null;
    this.#warmer.clear();
    if (this.#warmTimer !== undefined) window.clearTimeout(this.#warmTimer);
  }

  /**
   * Shows a same-product variant's image without waiting for section HTML.
   * @param {string} mediaId
   * @param {string} variantId
   * @returns {boolean} Whether the gallery owns the target media.
   */
  showVariantMedia(mediaId, variantId) {
    if (!mediaId || !variantId || !this.slideshow) return false;
    const generation = ++this.#mediaGeneration;
    this.#latestVariantId = variantId;
    this.#localMediaFailed = false;
    this.#pendingServerGallery = null;

    const slides = this.slideshow.refs.slides || [];
    const first = slides[0];
    if (!first) return false;

    if (this.dataset.hideVariants === 'false') {
      // A desktop grid remains visible alongside the mobile slideshow; the
      // existing server path owns its complete presentation in this mode.
      if (this.querySelector('.media-gallery__grid')) return false;
      const index = slides.findIndex((slide) =>
        [...slide.querySelectorAll('.product-media[data-media-id]')].some((media) => media.dataset.mediaId === mediaId));
      if (index < 0) return false;
      void this.slideshow.select(index, undefined, { animate: false });
      return true;
    }

    if (first.querySelector('.product-media')?.getAttribute('data-media-id') === mediaId) {
      void this.slideshow.select(0, undefined, { animate: false });
      return true;
    }

    // Reuse the existing server gallery when the hero changes media type.
    if (!first.classList.contains('product-media-container--image') ||
        this.querySelector('.media-gallery__grid .product-media-container')?.classList.contains('product-media-container--image') === false ||
        this.refs.zoomDialogComponent?.refs.media?.[0]?.classList.contains('product-media-container--image') === false) return false;

    const template = this.#findMediaTemplate(mediaId);
    const sourceMedia = template?.content.querySelector('.product-media');
    const image = sourceMedia?.querySelector('img');
    if (!(image instanceof HTMLImageElement) || !first.querySelector('.product-media')) return false;

    const source = this.#imageSource(image);
    const apply = () => {
      if (generation !== this.#mediaGeneration || !this.isConnected) return;
      this.#replaceHeroMedia(sourceMedia, image, mediaId);
      this.#pendingServerGallery = null;
      void this.slideshow?.select(0, undefined, { animate: false });
    };

    if (this.#warmer.isReady(source)) {
      apply();
    } else {
      void this.#warmer.warm(source).then((ready) => {
        if (generation !== this.#mediaGeneration) return;
        if (ready) apply();
        else {
          this.#localMediaFailed = true;
          this.#applyPendingServerGallery(generation);
        }
      });
    }
    return true;
  }

  /** Cancels a pending local image when a choice has no usable media. */
  cancelVariantMedia() {
    this.#mediaGeneration++;
    this.#latestVariantId = '';
    this.#pendingServerGallery = null;
  }

  /** @param {number} generation */
  #applyPendingServerGallery(generation) {
    const pending = this.#pendingServerGallery;
    if (pending?.generation !== generation || generation !== this.#mediaGeneration) return;
    this.#pendingServerGallery = null;
    this.replaceWith(pending.gallery);
  }

  /** @param {string} mediaId */
  #findMediaTemplate(mediaId) {
    return [...this.querySelectorAll('template[data-variant-hero-media-id]')]
      .find((item) => item.dataset.variantHeroMediaId === mediaId);
  }

  /** @param {HTMLImageElement} image */
  #imageSource(image) {
    return { src: image.src, srcset: image.srcset, sizes: image.sizes };
  }

  /**
   * Keeps the carousel, desktop grid, controls, and zoom's first media in sync.
   * @param {Element} sourceMedia
   * @param {HTMLImageElement} image
   * @param {string} mediaId
   */
  #replaceHeroMedia(sourceMedia, image, mediaId) {
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
    const slide = this.slideshow?.refs.slides?.[0];
    const gridItem = this.querySelector('.media-gallery__grid .product-media-container');
    const zoomItem = this.refs.zoomDialogComponent?.refs.media?.[0];

    for (const container of [slide, gridItem, zoomItem]) {
      const current = container?.querySelector('.product-media');
      if (!current) continue;
      const next = sourceMedia.cloneNode(true);
      if (!(next instanceof HTMLElement)) continue;
      if (container === zoomItem) {
        const zoomImage = next.querySelector('img');
        zoomImage?.setAttribute('sizes', '100vw');
        zoomImage?.setAttribute('loading', 'lazy');
        zoomImage?.setAttribute('ref', 'image');
        zoomItem.id = `product-${mediaId}-1`;
      }
      current.replaceWith(next);
      if (Number.isFinite(ratio) && ratio > 0 && container.style.getPropertyValue('--media-preview-ratio')) {
        container.style.setProperty('--media-preview-ratio', String(ratio));
      }
    }

    if (Number.isFinite(ratio) && ratio > 0) {
      const slides = this.slideshow?.refs.slides || [];
      if (slides.some((item) => item.classList.contains('product-media-container--tallest'))) {
        let tallest = slides[0];
        for (const item of slides) {
          item.classList.remove('product-media-container--tallest');
          if (Number(item.style.getPropertyValue('--media-preview-ratio')) <
              Number(tallest.style.getPropertyValue('--media-preview-ratio'))) tallest = item;
        }
        tallest?.classList.add('product-media-container--tallest');
      }
    }

    const thumbnails = this.querySelectorAll(
      'slideshow-controls .slideshow-controls__thumbnail:first-child img, zoom-dialog .dialog-thumbnails-list__thumbnail:first-child img'
    );
    for (const thumbnail of thumbnails) {
      if (!(thumbnail instanceof HTMLImageElement)) continue;
      thumbnail.srcset = image.srcset;
      thumbnail.sizes = '144px';
      thumbnail.alt = image.alt;
      thumbnail.src = image.src;
      if (Number.isFinite(ratio) && ratio > 0 && slide?.style.getPropertyValue('--media-preview-ratio')) {
        const button = thumbnail.closest('button');
        button?.style.setProperty('--aspect-ratio', String(ratio));
        if (button?.style.getPropertyValue('--gallery-aspect-ratio')) {
          button.style.setProperty('--gallery-aspect-ratio', String(ratio));
        }
      }
    }
  }

  #scheduleWarmCandidates = () => {
    this.#warmTimer = window.setTimeout(() => {
      if (!this.isConnected) return;
      for (const template of this.querySelectorAll('template[data-variant-hero-media-id]')) {
        const image = template.content.querySelector('.product-media img');
        if (image instanceof HTMLImageElement) void this.#warmer.warm(this.#imageSource(image));
      }
    }, 0);
  };

  /** @param {Event} event */
  #warmProspectiveOption = (event) => {
    const target = event.target;
    const option = target instanceof HTMLLabelElement
      ? target.querySelector('input[data-option-name]')
      : target;
    if (!(option instanceof HTMLInputElement || option instanceof HTMLSelectElement)) return;
    const picker = option.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId) return;
    const script = picker.querySelector('script[data-variant-media-map]');
    if (!script?.textContent) return;
    let payload;
    try { payload = JSON.parse(script.textContent); } catch { return; }
    const selectedOptions = [...picker.querySelectorAll('input[data-option-name]:checked, select')].map((input) => ({
      name: input.dataset.optionName || input.selectedOptions?.[0]?.dataset.optionName,
      value: input.value,
    }));
    const prospective = option instanceof HTMLSelectElement ? option.selectedOptions[0] : option;
    const name = prospective?.dataset.optionName;
    if (!name) return;
    const current = selectedOptions.find((item) => item.name === name);
    if (current) current.value = prospective.value;
    else selectedOptions.push({ name, value: prospective.value });
    const variant = findVariantMedia(payload, selectedOptions);
    const template = variant && this.#findMediaTemplate(variant.mediaId);
    const image = template?.content.querySelector('.product-media img');
    if (image instanceof HTMLImageElement) void this.#warmer.warm(this.#imageSource(image));
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.variantId && this.#latestVariantId && detail.variantId !== this.#latestVariantId) return;
        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (shouldPreserveGallery(detail, this.dataset.productId, this.#latestVariantId, this.#localMediaFailed)) {
          this.#pendingServerGallery = { generation: this.#mediaGeneration, gallery: newMediaGallery };
          return;
        }
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
